/**
 * Prototyper build-stream timeline (persisted in prototyper_build_sessions.build_timeline JSON).
 */

import type {
  PrototyperBuildTimeline,
  PrototyperBuildTimelineV1,
  PrototyperBuildTimelineV2,
  PrototyperPhaseId,
  PrototyperSegmentKind,
  PrototyperTimelineItem,
  PrototyperTimelinePhase,
  PrototyperTimelineSegment,
  PrototyperTimelineStep,
  PrototyperTimelineStepStatus,
} from '@ideoz/core';

/** Alias for local helpers — canonical v2 shape. */
export type BuildTimelineV2 = PrototyperBuildTimelineV2;
/** @deprecated Use BuildTimelineV2 — kept for transitional imports */
export type BuildTimelineV1 = PrototyperBuildTimelineV1;
export type StepStatus = PrototyperTimelineStepStatus;
export type { PrototyperPhaseId };

const TERMINAL_SEGMENT_KINDS = new Set<PrototyperSegmentKind>(['finalizing', 'done']);

const SEGMENT_LABEL: Record<PrototyperSegmentKind, string> = {
  planning: 'Planning',
  exploring: 'Exploring code',
  writing_files: 'Writing files',
  build: 'Build and type check',
  fixing: 'Fixing issues',
  finalizing: 'Finalizing',
  done: 'Done',
};

export type ToolStepInput = {
  key: string;
  toolName: string;
  isComplete: boolean;
  args?: Record<string, unknown>;
  result?: string;
};

export type TimelineEvent =
  | { kind: 'tool'; step: ToolStepInput }
  | { kind: 'text'; key: string; text: string };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Detect failed WebContainer build output (matches auto-continue helper). */
export function isBuildToolResultFailure(result?: string): boolean {
  if (!result) return false;
  if (/^\s*Build succeeded\.?\s*$/i.test(result.trim())) return false;
  return (
    result.includes('TypeScript check failed') ||
    result.includes('TypeScript type-check failed') ||
    result.includes('Build failed') ||
    result.includes('npm install') ||
    result.includes('npm run build')
  );
}

function truncateDetail(text: string, max = 120): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function phaseById(t: PrototyperBuildTimelineV1, id: PrototyperPhaseId): PrototyperTimelinePhase | undefined {
  return t.phases.find((p) => p.id === id);
}

function markPhaseDone(phase: PrototyperTimelinePhase, endIso: string): void {
  phase.status = 'done';
  phase.endedAt = endIso;
  for (const s of phase.steps ?? []) {
    if (s.status === 'running') s.status = 'done';
  }
}

/** Migrate v1 phases → v2 segments for display / persistence upgrade. */
export function migrateTimelineV1ToV2(v1: PrototyperBuildTimelineV1): PrototyperBuildTimelineV2 {
  const segments: PrototyperTimelineSegment[] = [];
  const phaseToKind: Record<string, PrototyperSegmentKind> = {
    planning: 'planning',
    exploring: 'exploring',
    writing_files: 'writing_files',
    production_build: 'build',
    finalizing: 'finalizing',
    done: 'done',
  };

  for (const phase of v1.phases) {
    const kind = phaseToKind[phase.id];
    if (!kind) continue;
    if (kind === 'done' && phase.status === 'pending') continue;

    const items: PrototyperTimelineItem[] =
      phase.steps?.map((s) => ({
        key: s.key,
        title: s.title,
        status: s.status,
        toolName: s.toolName,
      })) ?? [];

    if (items.length === 0 && phase.status === 'pending' && kind !== 'done') continue;

    segments.push({
      key: `${kind}-m-${segments.length}`,
      kind,
      label: SEGMENT_LABEL[kind],
      status: phase.status,
      items: items.length ? items : undefined,
      startedAt: phase.startedAt,
      endedAt: phase.endedAt,
    });
  }

  return {
    schemaVersion: 2,
    collapsedDefault: v1.collapsedDefault,
    segments,
  };
}

/** Coerce persisted timeline to v2 (migrates v1). */
export function ensureTimeline(raw: unknown): PrototyperBuildTimelineV2 | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.schemaVersion === 2 && Array.isArray(o.segments)) {
    return raw as PrototyperBuildTimelineV2;
  }
  if (o.schemaVersion === 1 && Array.isArray(o.phases)) {
    return migrateTimelineV1ToV2(raw as PrototyperBuildTimelineV1);
  }
  return null;
}

/** @deprecated Use ensureTimeline */
export function ensureTimelineV1(raw: unknown): PrototyperBuildTimelineV1 | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.schemaVersion !== 1 || !Array.isArray(o.phases)) return null;
  return raw as PrototyperBuildTimelineV1;
}

export function createEmptyTimeline(): PrototyperBuildTimelineV2 {
  const now = new Date().toISOString();
  return {
    schemaVersion: 2,
    collapsedDefault: true,
    segments: [
      {
        key: 'planning-0',
        kind: 'planning',
        label: SEGMENT_LABEL.planning,
        status: 'running',
        startedAt: now,
        items: [],
      },
    ],
  };
}

/** Normalize Chef coverage gap objects for timeline display. */
export function formatCoverageGapText(g: unknown): string {
  if (typeof g === 'string') return g;
  if (g && typeof g === 'object' && 'detail' in g) {
    return String((g as { detail: unknown }).detail);
  }
  return JSON.stringify(g);
}

export type PlanCompletePlanPayload = {
  mode?: unknown;
  skills?: unknown;
  files?: unknown;
  language?: unknown;
};

/** Merge Phase 1 planComplete annotation into the v2 timeline (planning segment + coverage). */
export function applyPlanCompleteToTimeline(
  timeline: PrototyperBuildTimelineV2,
  plan: PlanCompletePlanPayload,
  coverageOk: boolean,
  rawGaps: unknown[],
): PrototyperBuildTimelineV2 {
  const now = new Date().toISOString();
  const next = structuredClone(timeline) as PrototyperBuildTimelineV2;
  const gaps = rawGaps.map(formatCoverageGapText);

  next.specCoverage = { ok: coverageOk, gaps };

  let planning = next.segments.find((s) => s.kind === 'planning');
  if (!planning) {
    planning = {
      key: 'planning-0',
      kind: 'planning',
      label: SEGMENT_LABEL.planning,
      status: 'running',
      startedAt: now,
      items: [],
    };
    next.segments.unshift(planning);
  }

  const files = Array.isArray(plan.files) ? plan.files : [];
  const mode = typeof plan.mode === 'string' ? plan.mode : undefined;
  const skills = Array.isArray(plan.skills) ? (plan.skills as string[]).join(', ') : undefined;
  const language = typeof plan.language === 'string' ? plan.language : undefined;

  const summary = [
    mode ? `Mode: ${mode}` : null,
    language ? `Language: ${language}` : null,
    skills ? `Skills: ${skills}` : null,
    files.length > 0 ? `${files.length} planned file(s)` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  if (summary) planning.body = summary;

  const fileItems: PrototyperTimelineItem[] = files.slice(0, 12).map((f, i) => {
    const file = f as { path?: string; purpose?: string };
    return {
      key: `plan-file-${i}`,
      title: typeof file.path === 'string' ? file.path : `File ${i + 1}`,
      detail: typeof file.purpose === 'string' ? file.purpose : undefined,
      status: 'done',
    };
  });
  if (files.length > 12) {
    fileItems.push({
      key: 'plan-file-more',
      title: `+${files.length - 12} more planned file(s)`,
      status: 'done',
    });
  }
  planning.items = fileItems;
  if (!coverageOk && gaps.length > 0) {
    const gapNote = `${gaps.length} spec coverage gap(s) — see details below.`;
    planning.body = planning.body ? `${planning.body} · ${gapNote}` : gapNote;
  }
  closeSegment(planning, now, 'done');

  return next;
}

/**
 * Preserve annotation-driven planning + specCoverage when rebuilding timeline from tool steps.
 */
export function mergeTimelinePreservingAnnotations(
  toolTimeline: PrototyperBuildTimelineV2,
  annotationTimeline: PrototyperBuildTimelineV2 | null | undefined,
): PrototyperBuildTimelineV2 {
  if (!annotationTimeline) return toolTimeline;

  const next = structuredClone(toolTimeline) as PrototyperBuildTimelineV2;

  const buildSucceeded = next.segments.some((s) => s.kind === 'build' && s.status === 'done');
  if (annotationTimeline.specCoverage && !buildSucceeded) {
    next.specCoverage = annotationTimeline.specCoverage;
  }

  const annotatedPlanning = annotationTimeline.segments.find((s) => s.kind === 'planning');
  const annotatedExploring = annotationTimeline.segments.find((s) => s.kind === 'exploring');

  if (annotatedPlanning?.items?.length) {
    const planningIdx = next.segments.findIndex((s) => s.kind === 'planning');
    const annotatedHasPlanFiles = annotatedPlanning.items?.some((i) => i.key.startsWith('plan-file-'));
    if (planningIdx >= 0) {
      const toolPlanning = next.segments[planningIdx];
      const toolHasPlanFiles = toolPlanning.items?.some((i) => i.key.startsWith('plan-file-'));
      if (!toolPlanning.items?.length || (annotatedHasPlanFiles && !toolHasPlanFiles)) {
        next.segments[planningIdx] = {
          ...annotatedPlanning,
          status: toolPlanning.status === 'running' ? 'running' : annotatedPlanning.status,
          body: annotatedPlanning.body || toolPlanning.body,
        };
      }
    } else if (next.segments.length === 0) {
      next.segments = [annotatedPlanning];
    } else if (annotatedHasPlanFiles) {
      next.segments.unshift(annotatedPlanning);
    }
  }

  mergeAnnotatedSegment(next, 'exploring', annotatedExploring);

  const writingStarted = next.segments.some(
    (s) =>
      (s.kind === 'writing_files' || s.kind === 'build') &&
      (s.status === 'running' || s.status === 'done' || s.status === 'error'),
  );
  if (writingStarted) {
    const exploring = next.segments.find((s) => s.kind === 'exploring');
    if (exploring?.status === 'running') {
      closeSegment(exploring, new Date().toISOString(), 'done');
    }
  }

  return next;
}

/** Upsert a server-side read/search event into the exploring segment. */
export function applyExploreEventToTimeline(
  timeline: PrototyperBuildTimelineV2,
  item: PrototyperTimelineItem,
): PrototyperBuildTimelineV2 {
  const now = new Date().toISOString();
  const next = structuredClone(timeline) as PrototyperBuildTimelineV2;

  let exploring = next.segments.find((s) => s.kind === 'exploring' && s.status === 'running');
  if (!exploring) {
    exploring = {
      key: `exploring-${next.segments.length}`,
      kind: 'exploring',
      label: SEGMENT_LABEL.exploring,
      status: 'running',
      startedAt: now,
      items: [],
    };
    const writeIdx = next.segments.findIndex((s) => s.kind === 'writing_files');
    const planningIdx = next.segments.findIndex((s) => s.kind === 'planning');
    const insertAt =
      writeIdx >= 0 ? writeIdx : planningIdx >= 0 ? planningIdx + 1 : next.segments.length;
    next.segments.splice(insertAt, 0, exploring);
  }

  upsertItem(exploring, { ...item, status: item.status ?? 'done' });
  return next;
}

function mergeAnnotatedSegment(
  next: PrototyperBuildTimelineV2,
  kind: PrototyperSegmentKind,
  annotated: PrototyperTimelineSegment | undefined,
): void {
  if (!annotated?.items?.length) return;

  let idx = next.segments.findIndex((s) => s.kind === kind);
  if (idx >= 0) {
    const existing = next.segments[idx]!;
    const mergedItems = [...(existing.items ?? [])];
    for (const item of annotated.items) {
      if (!mergedItems.some((i) => i.key === item.key)) {
        mergedItems.push(item);
      }
    }
    next.segments[idx] = {
      ...existing,
      items: mergedItems,
      status: existing.status === 'running' ? existing.status : annotated.status,
    };
    return;
  }

  const writeIdx = next.segments.findIndex((s) => s.kind === 'writing_files');
  const planningIdx = next.segments.findIndex((s) => s.kind === 'planning');
  const insertAt =
    kind === 'exploring'
      ? writeIdx >= 0
        ? writeIdx
        : planningIdx >= 0
          ? planningIdx + 1
          : next.segments.length
      : next.segments.length;
  next.segments.splice(insertAt, 0, { ...annotated });
}

function closeSegment(segment: PrototyperTimelineSegment, endIso: string, finalStatus?: StepStatus): void {
  if (finalStatus) {
    segment.status = finalStatus;
  } else if (segment.status === 'running') {
    segment.status = 'done';
  }
  segment.endedAt = endIso;
  for (const item of segment.items ?? []) {
    if (item.status === 'running') item.status = segment.status === 'error' ? 'error' : 'done';
  }
}

function openSegment(
  segments: PrototyperTimelineSegment[],
  kind: PrototyperSegmentKind,
  now: string,
): PrototyperTimelineSegment {
  const seg: PrototyperTimelineSegment = {
    key: `${kind}-${segments.length}`,
    kind,
    label: SEGMENT_LABEL[kind],
    status: 'running',
    startedAt: now,
    items: [],
  };
  segments.push(seg);
  return seg;
}

function lastSegmentOfKind(
  segments: PrototyperTimelineSegment[],
  kind: PrototyperSegmentKind,
): PrototyperTimelineSegment | undefined {
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i].kind === kind) return segments[i];
  }
  return undefined;
}

function upsertItem(segment: PrototyperTimelineSegment, item: PrototyperTimelineItem): void {
  if (!segment.items) segment.items = [];
  const idx = segment.items.findIndex((i) => i.key === item.key);
  if (idx >= 0) segment.items[idx] = item;
  else segment.items.push(item);
}

function closeExploringIfRunning(
  segments: PrototyperTimelineSegment[],
  now: string,
): void {
  const exploring = lastSegmentOfKind(segments, 'exploring');
  if (exploring?.status === 'running') {
    closeSegment(exploring, now, 'done');
  }
}

function exploringHasRunningItems(segment: PrototyperTimelineSegment): boolean {
  return segment.items?.some((i) => i.status === 'running') ?? false;
}

export function toolStepTitle(step: { toolName: string; args?: Record<string, unknown> }): string {
  if (step.toolName === 'writeFile') {
    const p = String(step.args?.path ?? '');
    return p ? `Write ${p}` : 'Write file';
  }
  if (step.toolName === 'searchReplace') {
    return `Patch ${String(step.args?.path ?? '')}`;
  }
  if (step.toolName === 'readFile') {
    const p = String(step.args?.path ?? '');
    return p ? `Read ${p}` : 'Read file';
  }
  if (step.toolName === 'searchFiles') {
    const q = String(step.args?.query ?? '');
    return q ? `Search "${q}"` : 'Search files';
  }
  if (step.toolName === 'build') return 'Build and type check';
  if (step.toolName === 'configureSession') return 'Configure session';
  return step.toolName;
}

function itemStatus(isComplete: boolean, hadError?: boolean): StepStatus {
  if (hadError) return 'error';
  if (isComplete) return 'done';
  return 'running';
}

/** Build chronological events from assistant message parts + tool steps. */
export function buildTimelineEventsFromMessages(
  messages: readonly unknown[],
  toolSteps: ToolStepInput[],
): TimelineEvent[] {
  const stepByKey = new Map(toolSteps.map((s) => [s.key, s]));

  let msg: Record<string, unknown> | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (isRecord(m) && m.role === 'assistant') {
      msg = m;
      break;
    }
  }

  if (!msg || !Array.isArray(msg.parts)) {
    return toolSteps.map((step) => ({ kind: 'tool', step }));
  }

  const events: TimelineEvent[] = [];
  for (const part of msg.parts) {
    if (!isRecord(part)) continue;
    if (part.type === 'text' && typeof part.text === 'string') {
      const text = part.text.trim();
      if (text && text !== 'Proceed with prototype generation in the workbench.') {
        events.push({ kind: 'text', key: `text-${events.length}`, text });
      }
      continue;
    }
    const t = part.type;
    const isTool = typeof t === 'string' && (t === 'dynamic-tool' || t.startsWith('tool-'));
    if (isTool) {
      const id = typeof part.toolCallId === 'string' ? part.toolCallId : undefined;
      if (id && stepByKey.has(id)) {
        events.push({ kind: 'tool', step: stepByKey.get(id)! });
      }
    }
  }

  const toolEventCount = events.filter((e) => e.kind === 'tool').length;
  if (toolEventCount === 0) {
    return toolSteps.map((step) => ({ kind: 'tool', step }));
  }

  // While the stream is open, assistant parts often lag behind client toolSteps.
  // Do not drop in-flight writeFile/build steps or the timeline freezes on Planning.
  if (toolEventCount < toolSteps.length) {
    const seen = new Set(
      events
        .filter((e): e is { kind: 'tool'; step: ToolStepInput } => e.kind === 'tool')
        .map((e) => e.step.key),
    );
    for (const step of toolSteps) {
      if (!seen.has(step.key)) {
        events.push({ kind: 'tool', step });
      }
    }
  }

  return events;
}

/** Build v2 timeline from tool steps and optional assistant text events. */
export function timelineFromEvents(events: TimelineEvent[]): PrototyperBuildTimelineV2 {
  const now = new Date().toISOString();
  const segments: PrototyperTimelineSegment[] = [];
  let lastBuildFailed = false;

  const ensurePlanning = (): PrototyperTimelineSegment => {
    const existing = lastSegmentOfKind(segments, 'planning');
    if (existing) return existing;
    return openSegment(segments, 'planning', now);
  };

  const ensureWriting = (): PrototyperTimelineSegment => {
    const existing = lastSegmentOfKind(segments, 'writing_files');
    if (existing && existing.status === 'running') return existing;
    return openSegment(segments, 'writing_files', now);
  };

  for (const event of events) {
    if (event.kind === 'text') {
      if (!lastBuildFailed) continue;
      let fixing = lastSegmentOfKind(segments, 'fixing');
      if (!fixing || fixing.status !== 'running') {
        fixing = openSegment(segments, 'fixing', now);
      }
      fixing.body = event.text;
      if (!event.text.endsWith('…')) {
        closeSegment(fixing, now);
      }
      continue;
    }

    const step = event.step;
    const hadError =
      step.toolName === 'build'
        ? isBuildToolResultFailure(step.result)
        : step.result?.toLowerCase().includes('failed') ?? false;
    const status = itemStatus(step.isComplete, hadError);

    if (step.toolName === 'configureSession') {
      const planning = ensurePlanning();
      const mode = String(step.args?.mode ?? 'website');
      const skills = Array.isArray(step.args?.skills)
        ? (step.args!.skills as string[]).join(', ')
        : undefined;
      const reasoning = typeof step.args?.reasoning === 'string' ? step.args.reasoning : undefined;
      if (reasoning) planning.body = reasoning;

      upsertItem(planning, {
        key: step.key,
        title: 'Configure session',
        detail: skills ? `${mode} · ${skills}` : mode,
        status,
        toolName: 'configureSession',
      });

      if (step.isComplete) closeSegment(planning, now);
      continue;
    }

    if (
      step.toolName === 'writeFile' ||
      step.toolName === 'searchReplace' ||
      step.toolName === 'deleteFile'
    ) {
      if (lastBuildFailed) {
        const fixing = lastSegmentOfKind(segments, 'fixing');
        if (fixing?.status === 'running') closeSegment(fixing, now);
        lastBuildFailed = false;
      }

      const planning = lastSegmentOfKind(segments, 'planning');
      if (planning?.status === 'running') closeSegment(planning, now);

      closeExploringIfRunning(segments, now);

      const writing = ensureWriting();
      const path = String(step.args?.path ?? '');
      const lastSlash = path.lastIndexOf('/');
      const fileName = lastSlash >= 0 ? path.slice(lastSlash + 1) : path;
      const dirName = lastSlash >= 0 ? path.slice(0, lastSlash) : undefined;
      const verb =
        step.toolName === 'writeFile'
          ? 'Write'
          : step.toolName === 'searchReplace'
            ? 'Patch'
            : 'Delete';
      upsertItem(writing, {
        key: step.key,
        // Directory goes in `detail` below — repeating the full path in both fields
        // reads as noise once files start nesting a few folders deep.
        title: fileName ? `${verb} ${fileName}` : toolStepTitle(step),
        detail: dirName || undefined,
        status,
        toolName: step.toolName,
      });
      continue;
    }

    if (step.toolName === 'build') {
      closeExploringIfRunning(segments, now);

      const writing = lastSegmentOfKind(segments, 'writing_files');
      if (writing?.status === 'running') closeSegment(writing, now);

      const buildSeg = openSegment(segments, 'build', now);
      upsertItem(buildSeg, {
        key: step.key,
        title: 'Build and type check',
        detail: hadError && step.result ? truncateDetail(step.result) : undefined,
        status,
        toolName: 'build',
      });

      if (step.isComplete) {
        closeSegment(buildSeg, now, hadError ? 'error' : 'done');
        lastBuildFailed = hadError;
        if (hadError) {
          const existingFixing = lastSegmentOfKind(segments, 'fixing');
          if (!existingFixing || existingFixing.status !== 'running') {
            openSegment(segments, 'fixing', now);
          }
        }
      }
    }
  }

  return {
    schemaVersion: 2,
    collapsedDefault: true,
    segments,
  };
}

export function timelineFromToolSteps(
  steps: ToolStepInput[],
  messages?: readonly unknown[],
): PrototyperBuildTimelineV2 {
  const events =
    messages && messages.length > 0
      ? buildTimelineEventsFromMessages(messages, steps)
      : steps.map((step) => ({ kind: 'tool' as const, step }));
  return timelineFromEvents(events);
}

export type MergeLiveTerminalOptions = {
  productionBuildOk: boolean;
  finalizeComplete: boolean;
};

/**
 * During a live turn: append a running Finalizing segment after production build succeeds.
 * On stream success: close Finalizing and append Done.
 */
export function mergeLiveTerminalSegments(
  t: PrototyperBuildTimelineV2,
  opts: MergeLiveTerminalOptions,
): PrototyperBuildTimelineV2 {
  const next = structuredClone(t) as PrototyperBuildTimelineV2;
  const now = new Date().toISOString();

  const lastBuild = [...next.segments].reverse().find((s) => s.kind === 'build');
  const buildSucceeded = lastBuild?.status === 'done';

  if (opts.productionBuildOk && buildSucceeded && !opts.finalizeComplete) {
    const existing = lastSegmentOfKind(next.segments, 'finalizing');
    if (!existing) {
      openSegment(next.segments, 'finalizing', now);
    } else if (existing.status !== 'done') {
      existing.status = 'running';
      existing.endedAt = undefined;
      existing.startedAt = existing.startedAt ?? now;
    }
  }

  if (opts.finalizeComplete) {
    for (const seg of next.segments) {
      if (seg.status === 'running' && seg.kind !== 'finalizing' && seg.kind !== 'done') {
        closeSegment(seg, now);
      }
    }

    let finalizing = lastSegmentOfKind(next.segments, 'finalizing');
    if (!finalizing) {
      next.segments.push({
        key: `finalizing-${next.segments.length}`,
        kind: 'finalizing',
        label: SEGMENT_LABEL.finalizing,
        status: 'done',
        startedAt: now,
        endedAt: now,
      });
    } else if (finalizing.status === 'running') {
      closeSegment(finalizing, now);
    } else if (finalizing.status !== 'done') {
      closeSegment(finalizing, now);
    }

    if (!next.segments.some((s) => s.kind === 'done')) {
      next.segments.push({
        key: `done-${next.segments.length}`,
        kind: 'done',
        label: SEGMENT_LABEL.done,
        status: 'done',
        startedAt: now,
        endedAt: now,
      });
    }
  }

  return next;
}

export function markStreamSuccess(t: PrototyperBuildTimelineV2): PrototyperBuildTimelineV2 {
  const next = structuredClone(t) as PrototyperBuildTimelineV2;
  const now = new Date().toISOString();

  for (const seg of next.segments) {
    if (seg.status === 'running' && seg.kind !== 'finalizing') {
      closeSegment(seg, now);
    }
  }

  // Post-build advisory gaps are not shown once the production build succeeded.
  delete next.specCoverage;

  return mergeLiveTerminalSegments(next, {
    productionBuildOk: true,
    finalizeComplete: true,
  });
}

export function markStreamError(t: PrototyperBuildTimelineV2): PrototyperBuildTimelineV2 {
  const next = structuredClone(t) as PrototyperBuildTimelineV2;
  const now = new Date().toISOString();
  for (const seg of next.segments) {
    if (seg.status === 'running' || seg.status === 'pending') {
      seg.status = 'error';
      seg.endedAt = now;
      for (const item of seg.items ?? []) {
        if (item.status === 'running') item.status = 'error';
      }
    }
  }
  return next;
}

/**
 * Stream finished without transport error but production build never succeeded.
 * Does not mark Finalizing/Done — those stay hidden until {@link markStreamSuccess}.
 */
export function markStreamEndedWithoutSuccessfulBuild(
  t: PrototyperBuildTimelineV2,
): PrototyperBuildTimelineV2 {
  const next = structuredClone(t);
  const now = new Date().toISOString();

  for (const seg of next.segments) {
    if (seg.status !== 'running') continue;

    if (seg.kind === 'exploring' && !exploringHasRunningItems(seg)) {
      closeSegment(seg, now, 'done');
    } else {
      closeSegment(seg, now, 'error');
    }
  }

  return next;
}

/** Whether a segment kind is terminal (hidden until build success). */
export function isTerminalSegmentKind(kind: PrototyperSegmentKind): boolean {
  return TERMINAL_SEGMENT_KINDS.has(kind);
}

/** Active segment label for live header summary. */
export function activeSegmentLabel(timeline: PrototyperBuildTimelineV2 | null | undefined): string {
  if (!timeline?.segments?.length) return 'Working';
  const running = [...timeline.segments].reverse().find((s) => s.status === 'running');
  if (running) return running.label;
  const last = timeline.segments[timeline.segments.length - 1];
  return last?.label ?? 'Working';
}

/** Normalize any persisted timeline to v2 for UI. */
export function normalizeTimeline(raw: PrototyperBuildTimeline | null | undefined): PrototyperBuildTimelineV2 | null {
  if (!raw) return null;
  return ensureTimeline(raw);
}

// ─── Legacy v1 incremental API (kept for tests / backward compat) ─────────────

export function applyToolToTimeline(
  prev: PrototyperBuildTimelineV1,
  args: {
    toolName: string;
    stepKey: string;
    title: string;
    isComplete: boolean;
    hadError?: boolean;
  },
): PrototyperBuildTimelineV1 {
  const next: PrototyperBuildTimelineV1 = structuredClone(prev) as PrototyperBuildTimelineV1;
  const now = new Date().toISOString();
  const { toolName, stepKey, title, isComplete, hadError } = args;

  const planning = phaseById(next, 'planning');
  const writing = phaseById(next, 'writing_files');
  const productionBuild = phaseById(next, 'production_build');
  const finalizing = phaseById(next, 'finalizing');
  const donePh = phaseById(next, 'done');
  if (!planning || !writing || !productionBuild || !finalizing || !donePh) return prev;

  if (
    planning.status === 'running' &&
    (toolName === 'configureSession' || toolName === 'writeFile' || toolName === 'searchReplace')
  ) {
    markPhaseDone(planning, now);
    writing.startedAt = writing.startedAt ?? now;
    writing.status = 'running';
  }

  if (toolName === 'writeFile' || toolName === 'searchReplace') {
    writing.status = 'running';
    if (!writing.steps) writing.steps = [];
    const idx = writing.steps.findIndex((s) => s.key === stepKey);
    const row: PrototyperTimelineStep = {
      key: stepKey,
      toolName,
      title,
      status: hadError ? 'error' : isComplete ? 'done' : 'running',
    };
    if (idx >= 0) writing.steps[idx] = row;
    else writing.steps.push(row);
  }

  if (toolName === 'build') {
    if (writing.status === 'running') markPhaseDone(writing, now);
    productionBuild.status = 'running';
    productionBuild.startedAt = productionBuild.startedAt ?? now;
    if (!productionBuild.steps) productionBuild.steps = [];
    const idx = productionBuild.steps.findIndex((s) => s.key === stepKey);
    const row: PrototyperTimelineStep = {
      key: stepKey,
      toolName: 'build',
      title: 'Run production build',
      status: hadError ? 'error' : isComplete ? 'done' : 'running',
    };
    if (idx >= 0) productionBuild.steps[idx] = row;
    else productionBuild.steps.push(row);
  }

  if (toolName === 'configureSession') {
    if (!planning.steps) planning.steps = [];
    const idx = planning.steps.findIndex((s) => s.key === stepKey);
    const row: PrototyperTimelineStep = {
      key: stepKey,
      toolName: 'configureSession',
      title: 'Configure session',
      status: hadError ? 'error' : isComplete ? 'done' : 'running',
    };
    if (idx >= 0) planning.steps![idx] = row;
    else planning.steps!.push(row);
  }

  return next;
}
